import { useEffect, useMemo, useState, useCallback } from "react";
import { api, API, formatApiError, tokenStore } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, LogIn, LogOut, XCircle, Lock, Filter, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

const KIND_LABEL = {
  login_success: { label: "Connexion réussie", cls: "text-emerald-700 bg-emerald-50 border-emerald-100", Icon: LogIn },
  login_failed: { label: "Échec connexion", cls: "text-rose-700 bg-rose-50 border-rose-100", Icon: XCircle },
  login_locked: { label: "Compte verrouillé", cls: "text-amber-800 bg-amber-50 border-amber-200", Icon: Lock },
  logout: { label: "Déconnexion", cls: "text-slate-700 bg-slate-100 border-slate-200", Icon: LogOut },
};

function KindBadge({ kind }) {
  const meta = KIND_LABEL[kind] || { label: kind, cls: "text-slate-700 bg-slate-100 border-slate-200", Icon: LogIn };
  const { Icon } = meta;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium border rounded-full px-2 py-0.5 ${meta.cls}`}>
      <Icon className="h-3 w-3" /> {meta.label}
    </span>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      timeZone: "Europe/Paris",
    });
  } catch { return iso; }
}

function shortUA(ua) {
  if (!ua) return "—";
  // Extract the most useful bit (browser / OS)
  const m = ua.match(/(Chrome|Firefox|Safari|Edge|Opera)\/[\d.]+/);
  const os = ua.match(/\((.*?)\)/);
  const browser = m ? m[0].split("/")[0] : "?";
  const osShort = os ? os[1].split(";")[0].trim() : "";
  return `${browser}${osShort ? " · " + osShort : ""}`;
}

export default function LoginJournal() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [since, setSince] = useState("30");
  const [kind, setKind] = useState("all");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { since_days: parseInt(since, 10) };
      if (kind !== "all") params.kind = kind;
      if (email.trim()) params.email = email.trim().toLowerCase();
      const [{ data: list }, { data: agg }] = await Promise.all([
        api.get("/login-events", { params }),
        api.get("/login-events/stats", { params: { since_days: parseInt(since, 10) } }),
      ]);
      setItems(list);
      setStats(agg?.by_kind || {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [since, kind, email]);

  useEffect(() => { load(); }, [load]);

  const total = useMemo(() => Object.values(stats).reduce((a, b) => a + b, 0), [stats]);

  const downloadCsv = () => {
    const token = tokenStore.get();
    const p = new URLSearchParams({ since_days: since });
    if (kind !== "all") p.set("kind", kind);
    if (email.trim()) p.set("email", email.trim().toLowerCase());
    // token can't ride in <a href>; use fetch → blob to keep the Authorization header
    fetch(`${API}/login-events/export.csv?${p.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (!r.ok) throw new Error(r.statusText); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `connexions_${since}j.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      })
      .catch(() => toast.error("Export CSV impossible"));
  };

  return (
    <div>
      <PageHeader
        title="Journal de connexion"
        subtitle="Trace en direct des connexions des employés : réussies, échouées, verrouillages et déconnexions. Conservé 180 jours."
        action={
          <div className="flex gap-2">
            <Button data-testid="journal-refresh" variant="outline" onClick={load} disabled={loading}>
              <RefreshCcw className="h-4 w-4 mr-1" /> Rafraîchir
            </Button>
            <Button data-testid="journal-export" onClick={downloadCsv} className="bg-[#0066FF] hover:bg-[#0052CC]">
              <Download className="h-4 w-4 mr-1" /> Export CSV
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5" data-testid="journal-stats">
        <StatCard label="Total" value={total} tone="slate" />
        <StatCard label="Réussies" value={stats.login_success || 0} tone="emerald" />
        <StatCard label="Échecs" value={stats.login_failed || 0} tone="rose" />
        <StatCard label="Verrouillés" value={stats.login_locked || 0} tone="amber" />
        <StatCard label="Déconnexions" value={stats.logout || 0} tone="slate" />
      </div>

      <Card className="p-4 mb-4 border-slate-200">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-slate-500" />
          <h3 className="font-heading font-semibold text-slate-900 text-sm">Filtres</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Période</Label>
            <Select value={since} onValueChange={setSince}>
              <SelectTrigger data-testid="journal-since"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Dernières 24h</SelectItem>
                <SelectItem value="7">7 derniers jours</SelectItem>
                <SelectItem value="30">30 derniers jours</SelectItem>
                <SelectItem value="90">90 derniers jours</SelectItem>
                <SelectItem value="180">180 derniers jours</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="journal-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="login_success">Connexions réussies</SelectItem>
                <SelectItem value="login_failed">Échecs</SelectItem>
                <SelectItem value="login_locked">Verrouillages</SelectItem>
                <SelectItem value="logout">Déconnexions</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Email (exact)</Label>
            <Input
              data-testid="journal-email"
              type="email"
              placeholder="filtrer par adresse e-mail…"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") load(); }}
            />
          </div>
        </div>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">Chargement…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="journal-empty">
            Aucun événement pour cette période / ce filtre.
          </div>
        ) : (
          <>
          <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date · Heure (Paris)</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Employé</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Adresse IP</TableHead>
                <TableHead>Navigateur</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((e) => (
                <TableRow key={e.id} data-testid={`journal-row-${e.id}`}>
                  <TableCell className="text-sm font-mono text-slate-700">{fmt(e.logged_at)}</TableCell>
                  <TableCell><KindBadge kind={e.kind} /></TableCell>
                  <TableCell className="text-sm">{e.user_name || <span className="text-slate-400">—</span>}</TableCell>
                  <TableCell className="text-sm text-slate-600">{e.email || "—"}</TableCell>
                  <TableCell className="text-sm font-mono text-slate-600">{e.ip || "—"}</TableCell>
                  <TableCell className="text-xs text-slate-500">{shortUA(e.user_agent)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          <div className="md:hidden divide-y divide-slate-100">
            {items.map((e) => (
              <div key={e.id} className="p-4" data-testid={`journal-card-${e.id}`}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="font-medium text-slate-900 text-sm truncate">{e.user_name || e.email || "—"}</div>
                  <KindBadge kind={e.kind} />
                </div>
                <div className="text-xs text-slate-500">{fmt(e.logged_at)}</div>
                <div className="text-xs text-slate-500 truncate">{e.email || "—"}</div>
                <div className="text-[11px] font-mono text-slate-400 mt-1">
                  {e.ip} · {shortUA(e.user_agent)}
                </div>
              </div>
            ))}
          </div>
          </>
        )}
      </Card>
    </div>
  );
}

function StatCard({ label, value, tone }) {
  const toneCls = {
    slate: "bg-white border-slate-200 text-slate-900",
    emerald: "bg-emerald-50 border-emerald-100 text-emerald-900",
    rose: "bg-rose-50 border-rose-100 text-rose-900",
    amber: "bg-amber-50 border-amber-100 text-amber-900",
  }[tone] || "";
  return (
    <div className={`rounded-lg border p-3 ${toneCls}`} data-testid={`stat-${label.toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
      <div className="text-2xl font-heading font-bold mt-1">{value}</div>
    </div>
  );
}
